import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Link } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import List from '@mui/material/List';
import ListItemIcon from '@mui/material/ListItemIcon';
import Helmet from '../../../components/Helmet';
import CircularProgress from '../../../components/_loading/CircularProgress';
import useRoutes from '../../../components/_hook/useRoutes';
import Context from '../../../context';
import { useCompanyQuery } from '../../../../graphql/query/company/useCompanyQuery';
import NotFound from '../../../components/_error/NotFound';
import useKeepitalRoutes from '../../../components/_hook/useKeepitalRoutes';
import BusinessLayout from '../../../layout/Business';
import SubscribeCompanyPremiumAdvertisement from '../../../adminComponents/_advertisement/InhouseAdvertisements';

const useStyles = makeStyles((theme: Theme) => ({
  summaryCard: {
    height: '100%',
  },
  summaryCardActionArea: {
    height: '100%',
    alignItems: 'flex-start',
    display: 'flex',
  },
  completionTypography: {
    color: ({ completion }: any) => {
      if (completion < 40) {
        return '#F92949';
      }
      if (completion >= 40 && completion < 80) {
        return '#FE801E';
      }
      return theme.palette.primary.main;
    },
  },
  listItemIconSuccess: {
    color: '#ffffff',
    width: '80px',
    backgroundColor: theme.palette.primary.main,
    justifyContent: 'center',
    position: 'absolute',
    right: 0,
    height: '100%',
    alignItems: 'center',
  },
  listItemIconWarning: {
    color: '#ffffff',
    width: '80px',
    backgroundColor: '#FE801E',
    justifyContent: 'center',
    position: 'absolute',
    right: 0,
    height: '100%',
    alignItems: 'center',
  },
  listItem: {
    position: 'relative',
  },
}));

function BusinessDashboard() {
  const { t } = useTranslation();
  const { company } = useContext(Context);
  const { path } = useRoutes();
  const { path: keepitalPath } = useKeepitalRoutes();
  const { data, loading } = useCompanyQuery(
    `
    _id
    verification
    origin
    slug
    contacts {
      emails {
        id
        verified
      }
      websites {
        id
        status
      }
    }
    infos {
      profile {
        la
        text
      }
      incorporate
      businesstype
      sales {
        ratio
        revenue
      }
      workforce
      commercial_practices {
        currencies_accepted
        payment_methods
        terms_of_delivery
      }
    }
    media {
      images {
        logo {
          filename
          status
        }
      }
    }
    statistic {
      branches
      showcase {
        products
        categories
      }
      keywords {
        classifications
        brands
      }
      teams {
        departments
        employees
      }
      follower
    }
    planData {
      _id
      status
    }
  `,
    {
      variables: {
        filter: {
          _id: company._id as string,
        },
        limit: 1,
      },
    }
  );

  const queryCompany = data ? data.companies.items[0] : null;
  const statistic =
    queryCompany && queryCompany.statistic ? queryCompany.statistic : null;
  const scores = {
    profile:
      queryCompany && queryCompany.infos
        ? Math.abs(
            !!queryCompany.infos.profile && !!queryCompany.infos.profile.length
              ? 5
              : 0
          ) +
          Math.abs(queryCompany.infos.incorporate ? 2 : 0) +
          Math.abs(
            !!queryCompany.infos.businesstype &&
              !!queryCompany.infos.businesstype.length
              ? 2
              : 0
          ) +
          Math.abs(
            !!queryCompany.infos.sales && !!queryCompany.infos.sales.ratio
              ? 1
              : 0
          ) +
          Math.abs(
            !!queryCompany.infos.sales && !!queryCompany.infos.sales.revenue
              ? 1
              : 0
          ) +
          Math.abs(queryCompany.infos.workforce ? 1 : 0) +
          Math.abs(
            !!queryCompany.infos.commercial_practices &&
              !!queryCompany.infos.commercial_practices.currencies_accepted &&
              !!queryCompany.infos.commercial_practices.currencies_accepted
                .length
              ? 1
              : 0
          ) +
          Math.abs(
            !!queryCompany.infos.commercial_practices &&
              !!queryCompany.infos.commercial_practices.payment_methods &&
              !!queryCompany.infos.commercial_practices.payment_methods.length
              ? 1
              : 0
          ) +
          Math.abs(
            !!queryCompany.infos.commercial_practices &&
              !!queryCompany.infos.commercial_practices.terms_of_delivery &&
              !!queryCompany.infos.commercial_practices.terms_of_delivery.length
              ? 1
              : 0
          )
        : 0,
    email:
      queryCompany &&
      !!queryCompany.contacts &&
      !!queryCompany.contacts.emails &&
      !!queryCompany.contacts.emails.length
        ? Math.abs(
            (15 / queryCompany.contacts.emails.length) *
              queryCompany.contacts.emails.filter((e) => e.verified).length
          )
        : 0,
    logo:
      queryCompany &&
      !!queryCompany.media &&
      !!queryCompany.media.images &&
      !!queryCompany.media.images.logo &&
      !!queryCompany.media.images.logo.length
        ? 5
        : 0,
    branch: Math.abs(statistic && statistic.branches ? 10 : 0),
    showcase: Math.abs(
      statistic && statistic.showcase && statistic.showcase.products ? 10 : 0
    ),
    category: Math.abs(
      statistic && statistic.showcase && statistic.showcase.categories ? 10 : 0
    ),
    classification: Math.abs(
      // eslint-disable-next-line no-nested-ternary
      statistic && statistic.keywords && statistic.keywords.classifications
        ? statistic.keywords.classifications <= 5
          ? Math.abs(statistic.keywords.classifications * 2)
          : 10
        : 0
    ),
    brand: Math.abs(
      statistic && statistic.keywords && statistic.keywords.brands ? 5 : 0
    ),
    department: Math.abs(
      statistic && statistic.teams && statistic.teams.departments ? 3 : 0
    ),
    employee: Math.abs(
      statistic && statistic.teams && statistic.teams.employees ? 7 : 0
    ),
    links: Math.abs(
      queryCompany &&
        !!queryCompany.contacts &&
        !!queryCompany.contacts.websites &&
        !!queryCompany.contacts.websites.length
        ? 5
        : 0
    ),
    premium: Math.abs(
      queryCompany && queryCompany.planData && queryCompany.planData.status
        ? 5
        : 0
    ),
  };

  const toDoList = [
    {
      path: 'business.contacts',
      label: 'verify_email',
      status: scores.email === 15,
      positiveScore: scores.email,
      negativeScore: -Math.abs(15 - scores.email),
    },
    {
      path: 'business.logo',
      label: 'business_logo',
      status: scores.logo === 5,
      positiveScore: scores.logo,
      negativeScore: -Math.abs(5 - scores.logo),
    },
    {
      path: 'business.profile',
      label: 'profile_and_detail',
      labelCount: 2,
      status: scores.profile === 15,
      positiveScore: scores.profile,
      negativeScore: -Math.abs(15 - scores.profile),
    },
    {
      path: 'business.branch',
      label: 'branch_address_and_contact',
      labelCount: 2,
      status: scores.branch === 10,
      positiveScore: scores.branch,
      negativeScore: -Math.abs(10 - scores.branch),
    },
    {
      path: 'business.contacts',
      label: 'website_and_link',
      labelCount: 2,
      status: scores.links === 5,
      positiveScore: scores.links,
      negativeScore: -Math.abs(5 - scores.links),
    },
    {
      path: 'business.departments',
      label: 'department',
      labelCount: 2,
      status: scores.department === 3,
      positiveScore: scores.department,
      negativeScore: -Math.abs(3 - scores.department),
    },
    {
      path: 'business.employees',
      label: 'employee',
      labelCount: 2,
      status: scores.employee === 7,
      positiveScore: scores.employee,
      negativeScore: -Math.abs(7 - scores.employee),
    },
    {
      path: 'product.categories',
      label: 'category',
      labelCount: 2,
      status: scores.category === 10,
      positiveScore: scores.category,
      negativeScore: -Math.abs(10 - scores.category),
    },
    {
      path: 'product.list',
      label: 'product_and_service',
      labelCount: 2,
      status: scores.showcase === 10,
      positiveScore: scores.showcase,
      negativeScore: -Math.abs(10 - scores.showcase),
    },
    {
      path: 'business.keyword.classifications',
      label: 'keyword',
      labelCount: 2,
      status: scores.classification === 10,
      positiveScore: scores.classification,
      negativeScore: -Math.abs(10 - scores.classification),
    },
    {
      path: 'business.keyword.brands',
      label: 'brand',
      labelCount: 2,
      status: scores.brand === 5,
      positiveScore: scores.brand,
      negativeScore: -Math.abs(5 - scores.brand),
    },
    {
      path: 'plan.subscriptions',
      label: 'premium_listing',
      status: scores.premium === 5,
      positiveScore: scores.premium,
      negativeScore: -Math.abs(5 - scores.premium),
    },
  ];

  const completion = Object.values(scores)
    .reduce((a, b) => a + b, 0)
    .toFixed(0);

  const classes = useStyles({ completion });

  if (loading) return <CircularProgress center color="primary" />;
  if (!queryCompany) return <NotFound />;

  return (
    <>
      <Helmet title={`${t('dashboard')} | ${t('business')}`} />
      <Grid container spacing={2}>
        <Grid item xs={12} container justifyContent="center" spacing={3}>
          <Grid item xs={12} container justifyContent="center" spacing={1}>
            <Grid item xs={12}>
              <Typography variant="h4" align="center" gutterBottom>
                {t('summary')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card className={classes.summaryCard}>
                <CardActionArea className={classes.summaryCardActionArea}>
                  <CardContent>
                    <Typography variant="h6" align="center" gutterBottom>
                      {t('status')}
                    </Typography>
                    <br />
                    {queryCompany.verification ? (
                      <>
                        <Typography
                          variant="h6"
                          align="center"
                          color="primary"
                          gutterBottom
                        >
                          {t('business_verified')}
                        </Typography>
                        <Typography variant="subtitle1" align="center">
                          {t(
                            'you_have_full_access_to_all_features_and_functions'
                          )}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography
                          variant="h6"
                          align="center"
                          color="primary"
                          gutterBottom
                        >
                          {t('business_unverified')}
                        </Typography>
                        <Typography variant="subtitle1" align="center">
                          {t('your_access_is_limited_and_restricted')}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card className={classes.summaryCard}>
                <CardActionArea className={classes.summaryCardActionArea}>
                  <CardContent>
                    <Typography variant="h6" align="center" gutterBottom>
                      {t('completion')}
                    </Typography>
                    <br />
                    <Typography
                      variant="h4"
                      className={classes.completionTypography}
                      align="center"
                      color="primary"
                    >
                      {completion}%
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card className={classes.summaryCard}>
                <CardActionArea
                  className={classes.summaryCardActionArea}
                  component="a"
                  target="_blank"
                  href={keepitalPath('company.follower.index', {
                    origin:
                      queryCompany && queryCompany.origin
                        ? queryCompany.origin.toLowerCase()
                        : '',
                    companySlug:
                      queryCompany && queryCompany.slug
                        ? queryCompany.slug
                        : '',
                  })}
                >
                  <CardContent>
                    <Typography variant="h6" align="center" gutterBottom>
                      {t('follower', {
                        count:
                          queryCompany &&
                          queryCompany.statistic &&
                          queryCompany.statistic.follower
                            ? queryCompany.statistic.follower
                            : 1,
                      })}
                    </Typography>
                    <br />
                    <Typography variant="h4" align="center" color="primary">
                      {queryCompany &&
                      queryCompany.statistic &&
                      queryCompany.statistic.follower
                        ? queryCompany.statistic.follower
                        : 0}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          </Grid>
          {(!queryCompany.planData || !queryCompany.planData.status) && (
            <Grid item xs={12} container justifyContent="center">
              <Grid item xs={12}>
                <SubscribeCompanyPremiumAdvertisement />
              </Grid>
            </Grid>
          )}

          <Grid item xs={12} container justifyContent="center" spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5" align="center" gutterBottom>
                {t('to_do_checklist')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <List disablePadding>
                  {toDoList.map((todo, index) => {
                    let to = '';

                    if (todo.path) {
                      to = path(todo.path, {
                        companyID: queryCompany._id,
                      });
                    } else {
                      to = '#';
                    }

                    return (
                      <React.Fragment key={todo.label}>
                        <ListItem
                          button
                          dense
                          className={classes.listItem}
                          divider={index !== toDoList.length - 1}
                          component={Link}
                          to={to}
                        >
                          <ListItemText>
                            {t(todo.label, { count: todo.labelCount })}
                          </ListItemText>
                          <ListItemIcon
                            className={
                              todo.status
                                ? classes.listItemIconSuccess
                                : classes.listItemIconWarning
                            }
                          >
                            {todo.status ? (
                              <>
                                <CheckIcon />
                                &nbsp;+{todo.positiveScore}%
                              </>
                            ) : (
                              <>
                                <PriorityHighIcon />
                                &nbsp;{todo.negativeScore}%
                              </>
                            )}
                          </ListItemIcon>
                        </ListItem>
                      </React.Fragment>
                    );
                  })}
                </List>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

export default () => <BusinessLayout main={<BusinessDashboard />} />;
